// Catálogo de «Agrega accesorios»: pestañas y buscador, hueco de Regalos, «Cambia por un kit» y los
// extras en renglones del mismo alto, por categoría. Un accesorio con variantes abre su modal.
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { PackageX } from 'lucide-react';
import { Button, Input } from '@/kit';
import { cn } from '@/lib/cn';
import {
    accessoryCatalogEntries,
    type ModelGroup,
} from '@/domains/sales/portal/catalog/lib/modelGroups';
import { isSoldOut } from '../catalogAvailability';
import {
    EXTRA_CATEGORY_FILTERS,
    extraCategoryOf,
    extraOffersOf,
    type ExtraCategory,
} from '../buildSale';
import type { CardSaleBenefits } from '../promotionSlots';
import type { CatalogItem } from '../types';
import { AccessoryVariantsModal } from './AccessoryVariantsModal';
import type { ExtraPieceAction } from './ExtraQuantityStepper';
import { ExtrasTable, type ExtraEntry } from './ExtrasTable';
import { KitSwapSection } from './KitSwapRow';

/** Orden de las secciones con «Todo». */
const SECTION_ORDER: Exclude<ExtraCategory, 'ALL'>[] = ['ACCESSORY', 'SIM', 'MODEM', 'SERVICE'];

const labelOf = (category: ExtraCategory): string =>
    EXTRA_CATEGORY_FILTERS.find((f) => f.value === category)?.label ?? category;

/** Qué acción de qué SKU está en vuelo o en fila. */
interface PendingExtra {
    sku: string;
    action: ExtraPieceAction;
}

export interface ExtrasCatalogProps {
    items: CatalogItem[];
    /** Piezas en la venta por SKU. */
    counts: ReadonlyMap<string, number>;
    loading: boolean;
    error: boolean;
    onRetry: () => void;
    /** Aparta UNA pieza del SKU. */
    onAdd: (sku: string) => Promise<void>;
    /** Suelta UNA pieza del SKU (la más reciente). */
    onRemove: (sku: string) => Promise<void>;
    /** Precio rebajado por pieza que pone Promociones; el catálogo no calcula promos. */
    benefits?: (item: CatalogItem) => CardSaleBenefits;
    /** Sin SureKeep confirmado no se pinta el chip Crédito/Contado. */
    showFinancing?: boolean;
    /** Kits con el teléfono de la venta (`kitSwapsFor`). */
    kits?: readonly CatalogItem[];
    /** Cambia la ancla por el kit. */
    onSwapKit?: (sku: string) => Promise<void>;
    /** Precio del kit con promo; `null` si ninguna lo baja. */
    kitDiscountedPrice?: (kit: CatalogItem) => number | null;
    /** Otra escritura de la venta en vuelo: «Elegir kit» espera. */
    swapDisabled?: boolean;
    /** Sección «Regalos», arriba de los kits y del catálogo. */
    gifts?: ReactNode;
    /** Pieza desde el modal de variantes; sin ellas, `onAdd` / `onRemove`. */
    onAddPiece?: (sku: string) => Promise<void>;
    onRemovePiece?: (sku: string) => Promise<void>;
    /** Al cerrar el modal de variantes, una vez, si se movió alguna pieza. */
    onVariantsDone?: () => void;
}

export function ExtrasCatalog({
    items,
    counts,
    loading,
    error,
    onRetry,
    onAdd,
    onRemove,
    benefits,
    showFinancing = true,
    kits = [],
    onSwapKit,
    kitDiscountedPrice,
    swapDisabled = false,
    gifts,
    onAddPiece,
    onRemovePiece,
    onVariantsDone,
}: ExtrasCatalogProps) {
    const [category, setCategory] = useState<ExtraCategory>('ALL');
    const [query, setQuery] = useState('');
    const [openGroupKey, setOpenGroupKey] = useState<string | null>(null);
    const allExtras = useMemo(() => extraOffersOf(items, 'ALL', ''), [items]);
    const visible = useMemo(() => extraOffersOf(items, category, query), [items, category, query]);
    const entryBySku = useMemo(() => {
        const map = new Map<string, ExtraEntry>();
        for (const entry of accessoryCatalogEntries(allExtras)) {
            if (entry.kind === 'item') map.set(entry.item.sku, entry);
            else for (const v of entry.group.variants) map.set(v.sku, entry);
        }
        return map;
    }, [allExtras]);
    // El grupo entero aunque la búsqueda solo atine a una variante, en la posición de la primera.
    const entries = useMemo(() => {
        const seen = new Set<string>();
        const out: ExtraEntry[] = [];
        for (const item of visible) {
            const entry = entryBySku.get(item.sku);
            if (entry === undefined || seen.has(entry.key)) continue;
            seen.add(entry.key);
            out.push(entry);
        }
        return out;
    }, [visible, entryBySku]);
    const tabs = useMemo(() => {
        const present = new Set(allExtras.map(extraCategoryOf));
        return EXTRA_CATEGORY_FILTERS.filter((t) => t.value === 'ALL' || present.has(t.value));
    }, [allExtras]);
    const openGroup = useMemo((): ModelGroup | null => {
        if (openGroupKey === null) return null;
        for (const entry of entryBySku.values()) {
            if (entry.kind === 'model' && entry.key === openGroupKey) return entry.group;
        }
        return null;
    }, [openGroupKey, entryBySku]);

    const [pending, setPending] = useState<readonly PendingExtra[]>([]);
    // Las escrituras van en fila, una pieza a la vez; cada SKU se apaga mientras la suya espera.
    const queue = useRef<Promise<unknown>>(Promise.resolve());
    const queued = useRef(new Set<string>());
    const movedInModal = useRef(false);

    const run = (sku: string, action: ExtraPieceAction, fn: (sku: string) => Promise<void>) => {
        if (queued.current.has(sku)) return;
        queued.current.add(sku);
        const entry: PendingExtra = { sku, action };
        setPending((prev) => [...prev, entry]);
        const done = (): void => {
            queued.current.delete(sku);
            setPending((prev) => prev.filter((p) => p !== entry));
        };
        queue.current = queue.current.then(() => fn(sku)).then(done, done);
    };
    const pendingOf = (sku: string): ExtraPieceAction | null =>
        pending.find((p) => p.sku === sku)?.action ?? null;
    const benefitsOf = (item: CatalogItem): CardSaleBenefits | undefined => benefits?.(item);

    const closeModal = (): void => {
        setOpenGroupKey(null);
        if (!movedInModal.current) return;
        movedInModal.current = false;
        // Avisa una sola vez, cuando ya terminó la fila de piezas.
        void queue.current.then(() => onVariantsDone?.());
    };

    const tableProps = {
        counts,
        pendingOf,
        benefits: benefitsOf,
        showFinancing,
        onAdd: (sku: string) => {
            run(sku, 'add', onAdd);
        },
        onRemove: (sku: string) => {
            run(sku, 'remove', onRemove);
        },
        onChooseGroup: (group: ModelGroup) => {
            setOpenGroupKey(`model:${group.key}`);
        },
    };

    const sections =
        category === 'ALL'
            ? SECTION_ORDER.map((section) => ({
                  category: section,
                  entries: entries.filter(
                      (e) =>
                          extraCategoryOf(e.kind === 'item' ? e.item : e.group.variants[0]) ===
                          section,
                  ),
              })).filter((s) => s.entries.length > 0)
            : [{ category, entries }];

    return (
        <section aria-label="Extras" className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                {tabs.length > 2 && (
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Categoría">
                        {tabs.map((tab) => (
                            <button
                                key={tab.value}
                                type="button"
                                aria-pressed={category === tab.value}
                                onClick={() => {
                                    setCategory(tab.value);
                                }}
                                className={cn(
                                    'rounded-full border px-3 py-1 text-[12px] font-medium transition-colors',
                                    category === tab.value
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border bg-card text-foreground hover:border-primary/50',
                                )}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}
                <Input
                    type="search"
                    className="min-w-48 flex-1"
                    placeholder="Busca un accesorio, SIM o servicio"
                    aria-label="Buscar extra"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        // Enter agrega la primera coincidencia con existencia (o abre sus variantes).
                        const first = entries.find((entry) =>
                            entry.kind === 'item' ? !isSoldOut(entry.item) : !entry.group.soldOut,
                        );
                        if (first === undefined) return;
                        if (first.kind === 'model') setOpenGroupKey(first.key);
                        else run(first.item.sku, 'add', onAdd);
                        setQuery('');
                    }}
                />
            </div>
            {gifts}
            {onSwapKit !== undefined && (
                <KitSwapSection
                    kits={kits}
                    items={items}
                    showFinancing={showFinancing}
                    discountedPriceOf={kitDiscountedPrice}
                    disabled={swapDisabled}
                    onSwap={onSwapKit}
                />
            )}
            {loading ? (
                <ExtrasTable {...tableProps} entries={[]} caption="Cargando catálogo" loading />
            ) : error ? (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border py-8 text-center">
                    <PackageX className="size-7 text-muted-foreground" aria-hidden />
                    <p className="text-[13px] text-muted-foreground">
                        No se pudo leer el catálogo de la tienda.
                    </p>
                    <Button size="sm" variant="secondary" onClick={onRetry}>
                        Volver a intentar
                    </Button>
                </div>
            ) : entries.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">Ningún extra coincide.</p>
            ) : (
                sections.map((section) => (
                    <section
                        key={section.category}
                        aria-label={labelOf(section.category)}
                        className="flex flex-col gap-1.5"
                    >
                        {category === 'ALL' && (
                            <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                {labelOf(section.category)}
                            </h3>
                        )}
                        <div className="overflow-hidden rounded-lg border border-border bg-card">
                            <ExtrasTable
                                {...tableProps}
                                entries={section.entries}
                                caption={labelOf(section.category)}
                            />
                        </div>
                    </section>
                ))
            )}
            <AccessoryVariantsModal
                group={openGroup}
                counts={counts}
                pendingOf={pendingOf}
                benefits={benefitsOf}
                onAdd={(sku) => {
                    movedInModal.current = true;
                    run(sku, 'add', onAddPiece ?? onAdd);
                }}
                onRemove={(sku) => {
                    movedInModal.current = true;
                    run(sku, 'remove', onRemovePiece ?? onRemove);
                }}
                onClose={closeModal}
            />
        </section>
    );
}
